/**
 * Calibration samples: compact storage format + strict validation on import.
 *
 * Format (JSON):
 *   { "v": 1, "samples": [ { "c": "7", "s": [[x0, y0, x1, y1, …], …] }, … ] }
 *
 * Each stroke is a flat [x, y, …] list. Samples are stored normalised to a
 * unit box (larger side = 1, aspect kept), rounded to 3 decimals and thinned
 * to at most MAX_POINTS_PER_STROKE points, so a full calibration set is a few
 * kilobytes. The recogniser only ever needs the shape.
 */
import type { HwChar, HwStroke } from '../../contracts.ts';
import { type Polyline, type Pt, boundsOf, resampleLine } from './geometry.ts';
import { isHwChar } from './templates.ts';

export const SAMPLES_FORMAT_VERSION = 1;
export const MAX_POINTS_PER_STROKE = 40;

export interface UserSample {
  char: HwChar;
  /** Normalised strokes (unit box, top-left at 0,0). */
  strokes: Pt[][];
}

const round3 = (v: number) => Math.round(v * 1000) / 1000;

/** Normalise raw pad strokes into a storable sample (null when there's no ink). */
export function toUserSample(char: HwChar, strokes: readonly Polyline[]): UserSample | null {
  const thin = strokes
    .filter((s) => s.length > 0)
    .map((s) => (s.length > MAX_POINTS_PER_STROKE ? resampleLine(s, MAX_POINTS_PER_STROKE) : s));
  if (!thin.length) return null;
  // Normalise after thinning, so the stored sample spans exactly the unit box.
  const box = boundsOf(thin);
  const size = Math.max(box.w, box.h) || 1;
  const norm = thin.map((s) => s.map((p) => ({ x: round3((p.x - box.x) / size), y: round3((p.y - box.y) / size) })));
  return { char, strokes: norm };
}

export function serializeSamples(samples: readonly UserSample[]): string {
  return JSON.stringify({
    v: SAMPLES_FORMAT_VERSION,
    samples: samples.map((s) => ({ c: s.char, s: s.strokes.map((st) => st.flatMap((p) => [p.x, p.y])) })),
  });
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

function parseStroke(v: unknown): Pt[] | null {
  if (!Array.isArray(v) || v.length < 2 || v.length % 2 !== 0) return null;
  const pts: Pt[] = [];
  for (let i = 0; i < v.length; i += 2) {
    const x: unknown = v[i];
    const y: unknown = v[i + 1];
    if (typeof x !== 'number' || typeof y !== 'number' || !Number.isFinite(x) || !Number.isFinite(y)) return null;
    pts.push({ x, y });
  }
  return pts;
}

/**
 * Parse exported samples. Throws on malformed JSON or an unknown format
 * version; silently drops individual samples that don't validate (unknown
 * character, non-numeric points), so one bad entry can't lose the rest.
 */
export function parseSamples(json: string): UserSample[] {
  const data: unknown = JSON.parse(json);
  if (!isRecord(data) || data.v !== SAMPLES_FORMAT_VERSION || !Array.isArray(data.samples)) {
    throw new Error('Unrecognised handwriting samples format');
  }
  const out: UserSample[] = [];
  for (const item of data.samples as unknown[]) {
    if (!isRecord(item) || typeof item.c !== 'string' || !isHwChar(item.c) || !Array.isArray(item.s)) continue;
    const strokes = (item.s as unknown[]).map(parseStroke);
    if (!strokes.length || strokes.some((s) => s === null)) continue;
    out.push({ char: item.c, strokes: strokes.filter((s) => s !== null) });
  }
  return out;
}

/**
 * Convert a stored sample back into pad-like strokes, `size` pad units across
 * its larger side (t is irrelevant to recognition). Useful for previews and tests.
 */
export const sampleToStrokes = (s: UserSample, size = 1): HwStroke[] =>
  s.strokes.map((st) => st.map((p) => ({ x: p.x * size, y: p.y * size, t: 0 })));
